    if (!customElements.get('product-upsell')) {
      // Parent products we've observed sitting in the cart next to their upsell.
      const SEEN_PARENTS_KEY = 'upsell_parents_seen';
      // Store-level Okendo subscriber id. Public: it ships inside every PDP's
      // Okendo widget markup; used only to read approved reviews.
      const OKENDO_STORE_ID = 'c7b2c369-e177-4ef6-acd8-d891ec1b0a13';

      class ProductUpsell extends HTMLElement {
        constructor() {
          super();
          this.formId = this.dataset.formId;
          this.sectionId = this.dataset.sectionId;
          this.miniCart = document.querySelector('mini-cart');

          this.discountEnabled = this.dataset.discountEnabled === 'true';
          this.priceMap = this.parsePriceMap(this.dataset.priceMap);

          this.convGoalId = this.dataset.convGoalId;
          this.trackedConversions = new Set();

          // Number of add/remove requests in flight. While non-zero the cart is
          // mid-change, so state syncs are skipped to avoid clobbering the UI.
          this.pending = 0;

          // Synthetic Smart Cart lines painted at tap time, keyed by their
          // optimistic line key; each entry is removed when its add settles.
          this._optimisticItems = new Map();
          this._optimisticReassert = null;

          this.handleCartUpdate = this.handleCartUpdate.bind(this);
          this.handleUpsellToggle = this.handleUpsellToggle.bind(this);
          this.handleInfoTrigger = this.handleInfoTrigger.bind(this);
          this.handleInfoKeydown = this.handleInfoKeydown.bind(this);
          this.handleInfoAtc = this.handleInfoAtc.bind(this);
          this.closeInfo = this.closeInfo.bind(this);

          this.bindToggles();
          this.bindInfoDrawer();

          if (this.discountEnabled) {
            this.renderDiscountedPrices();
          }

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
          }

          // A ticked card means "this is in the cart", so seed the selection
          // from the cart the visitor already has — off the critical path.
          const seed = () => {
            this.refreshFromCart();
            this.loadRecommendation();
            this.observeImpression();
          };
          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(seed, { timeout: 2000 });
          } else {
            setTimeout(seed, 0);
          }
        }

        connectedCallback() {
          // Stamped here, not in the constructor: a custom element constructor
          // must not add attributes, and `class` is one. Runs on upgrade, well
          // before the idle seed that reads this._layout.
          this.applyLayoutMode();
          this.bindSwipeGuard();
          this.bindRowArrows();
        }

        // Desktop can't swipe a row, so hover-capable devices get prev/next
        // arrows over the track (touch devices keep swiping; the gallery in
        // the More-info drawer makes the same split). Each press moves one
        // card; an arrow hides at its end of the track.
        bindRowArrows() {
          if (this._rowArrowsBound || !this.isRowLayout()) return;
          if (window.matchMedia('(hover: none)').matches) return;
          const wrap = this.querySelector('.product-upsell__row');
          const track = wrap && wrap.querySelector('.product-upsell__list');
          if (!wrap || !track) return;
          this._rowArrowsBound = true;

          const makeArrow = (dir) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'product-upsell__nav product-upsell__nav--' + (dir < 0 ? 'prev' : 'next');
            b.setAttribute('aria-label', dir < 0 ? 'Previous products' : 'Next products');
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('viewBox', '0 0 8 12');
            svg.setAttribute('aria-hidden', 'true');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', dir < 0 ? 'M7 1L1.5 6 7 11' : 'M1 1l5.5 5L1 11');
            path.setAttribute('fill', 'none');
            path.setAttribute('stroke', 'currentColor');
            path.setAttribute('stroke-width', '1.8');
            path.setAttribute('stroke-linecap', 'round');
            path.setAttribute('stroke-linejoin', 'round');
            svg.appendChild(path);
            b.appendChild(svg);
            b.addEventListener('click', () => {
              const card = track.querySelector('.product-upsell__card');
              const gap = parseFloat(getComputedStyle(track).columnGap) || 8;
              const step = card ? card.offsetWidth + gap : track.clientWidth;
              this._navSource = 'arrow';
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              track.scrollBy({ left: dir * step, behavior: reduce ? 'auto' : 'smooth' });
            });
            return b;
          };
          const prev = makeArrow(-1);
          const next = makeArrow(1);
          wrap.appendChild(prev);
          wrap.appendChild(next);

          const update = () => {
            prev.classList.toggle('is-hidden', track.scrollLeft <= 2);
            next.classList.toggle('is-hidden', track.scrollLeft + track.clientWidth >= track.scrollWidth - 2);
          };
          this._updateRowNav = update;
          track.addEventListener('scroll', update, { passive: true });
          window.addEventListener('resize', update, { passive: true });
          update();
        }

        disconnectedCallback() {
          if (typeof this.unsubscribe === 'function') this.unsubscribe();
        }

        // --upsell-tap:add makes the whole card add to cart, so in the slider a
        // drag that the browser does not classify as a scroll would add a
        // product the shopper was only swiping past. Suppress the click if the
        // track actually moved. Slider arm only; the stack never scrolls.
        bindSwipeGuard() {
          if (this._swipeGuardBound) return;
          this._swipeGuardBound = true;
          const track = this.querySelector('.product-upsell__list');
          if (!track) return;
          let startLeft = 0;
          track.addEventListener('pointerdown', () => { startLeft = track.scrollLeft; this._navSource = ''; }, { passive: true });

          // The row arm's hypothesis is "shoppers see more items by swiping", so
          // the swipe itself has to be measurable or a null result is
          // uninterpretable. One event per page view, fired the first time the
          // track has moved a card's worth. Intelligems reads window.igEvents
          // (JS-type custom event, identifier == pushed name); GA4/Clarity go
          // through the shared tracker.
          let swiped = false;
          track.addEventListener('scroll', () => {
            if (swiped || !this.isRowLayout() || track.scrollLeft < 40) return;
            swiped = true;
            try { (window.igEvents = window.igEvents || []).push({ event: 'upsell_row_swipe' }); } catch (err) {}
            this.trackEngagement('upsell_row_swipe', { upsell_layout: this._layout, upsell_nav: this._navSource || 'swipe', upsell_parent: this.dataset.productHandle });
          }, { passive: true });
          track.addEventListener('click', (event) => {
            if (!this.isRowLayout()) return;
            if (Math.abs(track.scrollLeft - startLeft) <= 3) return;
            event.preventDefault();
            event.stopPropagation();
          }, true);
        }

        getSelectedVariantIds() {
          return this.getSelectedInputs()
            .map((input) => parseInt(input.value, 10))
            .filter((id) => !Number.isNaN(id));
        }

        getSelectedInputs() {
          return Array.from(this.querySelectorAll('.product-upsell__checkbox:checked'));
        }

        getVariation() {
          const COOKIE = 'upsell_ab_variation';
          const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE + '=([^;]*)'));
          if (match && (match[1] === 'A' || match[1] === 'B')) return match[1];

          const variation = Math.floor(Math.random() * 2) === 0 ? 'A' : 'B';
          const maxAge = 60 * 60 * 24 * 30;
          document.cookie = `${COOKIE}=${variation}; path=/; max-age=${maxAge}; SameSite=Lax`;
          return variation;
        }

        parsePriceMap(raw) {
          const map = {};
          if (!raw) return map;
          raw.split('\n').forEach((line) => {
            const parts = line.split(':');
            const handle = (parts[0] || '').trim();
            const a = parseFloat(parts[1]);
            const b = parseFloat(parts[2]);
            if (!handle) return;
            map[handle] = {
              A: Number.isNaN(a) ? null : a,
              B: Number.isNaN(b) ? null : b,
            };
          });
          return map;
        }

        resolvePrice(handle, variation) {
          const entry = this.priceMap[handle];
          if (!entry) return null;
          const price = variation === 'B' ? entry.B : entry.A;
          return price == null ? null : price;
        }

        presentmentRate() {
          const rate = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate);
          return rate && rate > 0 ? rate : 1;
        }

        formatMoney(cents) {
          const format = (window.theme && theme.shopSettings && theme.shopSettings.moneyFormat) || '${{amount}}';
          if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
            return theme.Currency.formatMoney(cents, format);
          }

          // Fallback: handle Shopify's money_format placeholders.
          const value = Math.abs(Number(cents));
          const withCommas = (number) => number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

          return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
            switch (placeholder) {
              case 'amount':
                return withCommas((value / 100).toFixed(2));
              case 'amount_no_decimals':
                return withCommas(Math.round(value / 100).toString());
              case 'amount_with_comma_separator':
                return (value / 100).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
              case 'amount_no_decimals_with_comma_separator':
                return Math.round(value / 100).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
              case 'amount_with_space_separator':
                return (value / 100).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1 ');
              case 'amount_no_decimals_with_space_separator':
                return Math.round(value / 100).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1 ');
              default:
                return withCommas((value / 100).toFixed(2));
            }
          });
        }

        renderDiscountedPrices() {
          const variation = this.getVariation();
          this.querySelectorAll('.product-upsell__price-group').forEach((group) => {
            const handle = group.dataset.productHandle;
            const price = this.resolvePrice(handle, variation);
            if (price == null) return;
            const cents = Math.round(price * this.presentmentRate()) * 100;
            const target = group.querySelector('[data-upsell-price]');
            if (target) target.textContent = this.formatMoney(cents);

            // Show the original variant price as a struck-through compare price.
            if (!group.querySelector('.product-upsell__price--compare')) {
              const variantPrice = parseInt(group.dataset.variantPrice, 10);
              if (!Number.isNaN(variantPrice) && variantPrice > cents) {
                const compare = document.createElement('span');
                compare.className = 'product-upsell__price product-upsell__price--compare';
                compare.textContent = this.formatMoney(variantPrice);
                group.insertBefore(compare, group.firstChild);
                if (target) target.classList.add('product-upsell__price--sale');
              }
            }
          });
        }


        getParentProductId() {
          return this.dataset.productId || '';
        }

        handleCartUpdate(event) {
          if (!event || event.source === 'product-upsell') return;
          this.refreshFromCart();
        }


        bindToggles() {
          // Delegated, so a card injected by the recommendation engine is live
          // without re-binding anything.
          this.addEventListener('change', (event) => {
            const input = event.target.closest('.product-upsell__checkbox');
            if (input && this.contains(input)) this.handleUpsellToggle({ currentTarget: input });
          });
        }

        handleUpsellToggle(event) {
          const input = event.currentTarget;
          if (input.dataset.busy === 'true') {
            input.checked = !input.checked;
            return;
          }

          if (input.checked) {
            this.trackConversion(input);
            this.addUpsell(input);
          } else {
            this.removeUpsell(input);
          }
        }

        setCardLoading(input, state) {
          const card = input.closest('.product-upsell__card');
          if (card) card.classList.toggle('is-loading', state);
          if (state) {
            input.dataset.busy = 'true';
          } else {
            delete input.dataset.busy;
          }
          this.pending = Math.max(0, this.pending + (state ? 1 : -1));
        }

        cartSectionIds() {
          if (!this.miniCart || typeof this.miniCart.getSectionsToRender !== 'function') return [];
          return this.miniCart.getSectionsToRender().map((section) => section.id);
        }

        updateCartSections(parsedState) {
          if (!parsedState || !parsedState.sections) return;
          if (!this.miniCart || typeof this.miniCart.getSectionsToRender !== 'function') return;

          this.miniCart.getSectionsToRender().forEach((section) => {
            const target = document.getElementById(section.id);
            const html = parsedState.sections[section.id];
            if (!target || !html) return;
            const parsed = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector(section.selector || '.shopify-section');
            if (parsed) target.innerHTML = parsed.innerHTML;
          });
        }

        notifyCartUpdated() {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-upsell' });
          }
        }

        // ---- Optimistic Smart Cart line (perceived ATC speed, 2026-08-21) ----
        // Measured on live: /cart/add.js answers in ~1.0-1.5s of server time
        // (the sections payload is NOT the cost; bare adds time the same), and
        // Rebuy renders the new line only after its own /cart.json refetch,
        // ~1.4-1.6s after the tap. Painting a synthetic line through Rebuy's
        // own template (Cart.setCart re-renders synchronously) makes the add
        // feel instant; the refetch Rebuy fires on seeing the add response
        // then swaps in the real line at the same spot. Every step is
        // best-effort: if Rebuy is absent or anything throws, the original
        // wait-for-Rebuy behavior stands.

        rebuyCartAPI() {
          const R = window.Rebuy;
          if (!R || !R.Cart) return null;
          if (typeof R.Cart.getCart !== 'function' || typeof R.Cart.setCart !== 'function') return null;
          const cart = R.Cart.getCart();
          if (!cart || !Array.isArray(cart.items)) return null;
          return R;
        }

        buildOptimisticItem(input) {
          const variantId = parseInt(input.value, 10);
          const card = input.closest('.product-upsell__card');
          const priceGroup = card && card.querySelector('.product-upsell__price-group');
          const cents = priceGroup ? parseInt(priceGroup.dataset.variantPrice, 10) : NaN;
          if (!variantId || !Number.isFinite(cents)) return null;
          const img = card.querySelector('.product-upsell__image-tag');
          const src = (img && (img.currentSrc || img.src)) || '';
          const title = input.dataset.productTitle || '';
          const handle = input.dataset.productHandle || '';
          return {
            key: 'upsell-optimistic-' + variantId,
            id: variantId,
            variant_id: variantId,
            product_id: 0,
            title: title,
            product_title: title,
            variant_title: null,
            handle: handle,
            url: handle ? '/products/' + handle : '',
            quantity: 1,
            price: cents,
            line_price: cents,
            original_price: cents,
            original_line_price: cents,
            discounted_price: cents,
            final_price: cents,
            final_line_price: cents,
            total_discount: 0,
            discounts: [],
            line_level_discount_allocations: [],
            image: src,
            featured_image: { url: src, alt: title },
            requires_shipping: true,
            gift_card: false,
            taxable: true,
            vendor: 'Conscious Items',
            options_with_values: [],
            variant_options: [],
            // Never sent to the server. The leading underscore keeps Rebuy's
            // template from printing it, and the class Rebuy derives from it
            // (.property-key-_upsell_optimistic) is the styling hook that
            // makes the pending line inert.
            properties: { _upsell_optimistic: '1' },
          };
        }

        // Merge every still-pending synthetic line into Rebuy's current cart
        // and re-render. No-op when they are all already present, so the
        // rebuy:cart.change listener below cannot feed itself.
        applyOptimisticCart(R) {
          const cart = R.Cart.getCart();
          const pending = Array.from(this._optimisticItems.values())
            .filter((item) => !cart.items.some((line) => line.key === item.key));
          if (!pending.length) return;
          const addedCents = pending.reduce((sum, item) => sum + item.final_line_price, 0);
          const merged = Object.assign({}, cart);
          merged.items = pending.concat(cart.items);
          merged.item_count = (cart.item_count || 0) + pending.length;
          ['items_subtotal_price', 'total_price', 'original_total_price'].forEach((field) => {
            if (typeof cart[field] === 'number') merged[field] = cart[field] + addedCents;
          });
          R.Cart.setCart(merged);
        }

        bindOptimisticReassert() {
          if (this._optimisticReassert) return;
          // Rebuy's refetch after add #1 rebuilds its cart from server truth,
          // which would silently drop the still-pending line of a second card
          // tapped in quick succession. Re-assert pending lines whenever a
          // cart change leaves them out (guarded no-op otherwise).
          this._optimisticReassert = () => {
            try {
              if (!this._optimisticItems.size) return;
              const R = this.rebuyCartAPI();
              if (R) this.applyOptimisticCart(R);
            } catch (err) { /* best-effort */ }
          };
          document.addEventListener('rebuy:cart.change', this._optimisticReassert);
        }

        showOptimisticLine(input) {
          try {
            const R = this.rebuyCartAPI();
            if (!R) return null;
            const item = this.buildOptimisticItem(input);
            if (!item || this._optimisticItems.has(item.key)) return null;
            this._optimisticItems.set(item.key, item);
            this.bindOptimisticReassert();
            this.applyOptimisticCart(R);
            // Hard backstop: a hung add must not leave a phantom line around.
            setTimeout(() => {
              if (this._optimisticItems.has(item.key)) this.settleOptimisticLine(item.key, true);
            }, 12000);
            return { settle: (failed) => this.settleOptimisticLine(item.key, failed) };
          } catch (err) {
            return null;
          }
        }

        settleOptimisticLine(key, failed) {
          try {
            this._optimisticItems.delete(key);
            const R = this.rebuyCartAPI();
            if (!R) return;
            if (failed) {
              this.dropOptimisticLine(R, key);
              return;
            }
            // Success: Rebuy's watchCart refetch replaces the synthetic line
            // with the real one. Backstop a missed refetch by forcing server
            // truth once if the synthetic key is still rendered a beat later.
            setTimeout(() => {
              try {
                const R2 = this.rebuyCartAPI();
                if (!R2) return;
                const stillFake = R2.Cart.getCart().items.some((line) => line.key === key);
                if (stillFake && typeof R2.Cart.fetchShopifyCart === 'function') {
                  R2.Cart.fetchShopifyCart(() => {});
                }
              } catch (err) { /* best-effort */ }
            }, 3000);
          } catch (err) { /* best-effort */ }
        }

        dropOptimisticLine(R, key) {
          const cart = R.Cart.getCart();
          const removed = cart.items.find((line) => line.key === key);
          if (!removed) return;
          const merged = Object.assign({}, cart);
          merged.items = cart.items.filter((line) => line.key !== key);
          merged.item_count = Math.max(0, (cart.item_count || 0) - (removed.quantity || 1));
          ['items_subtotal_price', 'total_price', 'original_total_price'].forEach((field) => {
            if (typeof cart[field] === 'number') merged[field] = Math.max(0, cart[field] - (removed.final_line_price || 0));
          });
          R.Cart.setCart(merged);
        }

        buildLineItem(input) {
          const item = { id: parseInt(input.value, 10), quantity: 1 };

          // Attribution marker on EVERY widget add, independent of the discount
          // setting. Without it a widget add is indistinguishable from a normal
          // purchase of the same product, so attach rate can only be inferred
          // from noisy proxies. Leading underscore keeps it hidden from the
          // customer in cart/checkout/order.
          // A card is "recommended" when the engine put it there, whichever slot
          // it landed in: in all-rebuy mode slot 1 is engine-filled too
          // (fillSlot stamps data-upsell-recommended on the <li>), and tagging
          // it "pinned" would credit the engine's picks to a slot that no
          // longer exists in that arm.
          const recommended = !!input.closest('[data-upsell-recommended], [data-upsell-dynamic-slot]');
          const props = {
            _upsell_parent: String(this.getParentProductId()),
            _upsell_slot: recommended ? 'recommended' : 'pinned',
            _upsell_tap: this.tapMode(),
            _upsell_source: this._pendingAddSource || 'card',
            _upsell_host: String(this.getParentProductId()),
            _upsell_intent: recommended ? ((this._recIntents && this._recIntents[input.dataset.productHandle]) || this._recIntent || 'complementary') : 'pinned',
            _upsell_price_tier: recommended ? String((this._recTiers && this._recTiers[input.dataset.productHandle]) || '') : '',
            _upsell_rank: recommended ? String((this._recRanks && this._recRanks[input.dataset.productHandle]) ?? '') : '',
          };
          this._pendingAddSource = null;
          this.trackEngagement('upsell_add', {
            ci_intent: props._upsell_intent, ci_rank: props._upsell_rank, ci_host: props._upsell_host,
            upsell_product: input.dataset.productHandle,
            upsell_slot: props._upsell_slot,
            upsell_source: props._upsell_source,
            upsell_tap_mode: props._upsell_tap,
            upsell_parent: this.dataset.productHandle,
          });

          if (this.discountEnabled) {
            const variation = this.getVariation();
            const price = this.resolvePrice(input.dataset.productHandle, variation);
            props._upsell_variation = variation;
            if (price != null) props._upsell_price = String(Math.round(price * this.presentmentRate()));
          }

          item.properties = props;
          return item;
        }

        withSections(body) {
          const sections = this.cartSectionIds();
          if (sections.length) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          return body;
        }

        async addUpsell(input) {
          this.setCardLoading(input, true);
          // Open the cart on the tick itself, not after the network round-trip,
          // so it feels as fast as the main ATC button — with the tapped
          // product already painted in as a pending line.
          const optimistic = this.showOptimisticLine(input);
          this.openSmartCart();
          let failed = false;

          const lineItem = this.buildLineItem(input);

          /* MERGE duplicates (Ondrej 2026-08-31): Shopify splits lines whose
             properties differ, so adding a product here that was already in
             the cart from another path produced two identical-looking rows.
             When a line for this variant already exists, bump ITS quantity
             instead and hand it the upsell properties, so the cart shows one
             row and the discount/attribution still lands on it. Falls back to
             a normal add on any failure. */
          try {
            const existing = await this.findExistingLine(lineItem.id);
            if (existing) {
              const merged = Object.assign({}, existing.properties || {}, lineItem.properties || {});
              const changeBody = this.withSections({
                id: existing.key,
                quantity: (existing.quantity || 0) + (lineItem.quantity || 1),
                properties: merged,
              });
              const changeRes = await fetch(
                `${(window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change'}.js`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                  body: JSON.stringify(changeBody),
                }
              );
              if (changeRes.ok) {
                const changed = await changeRes.json();
                this.settleOptimistic(optimistic, changed);
                return;
              }
            }
          } catch (mergeErr) {
            /* fall through to the normal add */
          }

          const body = this.withSections({ items: [lineItem] });

          try {
            const response = await fetch(
              `${(window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add'}.js`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(body),
              }
            );

            const result = await response.json();
            if (!response.ok) throw new Error(result.description || result.message || 'Add to cart failed');

            const added = (result.items || [])[0];
            if (added && added.key) input.dataset.lineKey = added.key;

            this.updateCartSections(result);
            this.notifyCartUpdated();
          } catch (err) {
            console.error('[product-upsell] Failed to add upsell item', err);
            failed = true;
            input.checked = false;
          } finally {
            if (optimistic) optimistic.settle(failed);
            this.setCardLoading(input, false);
            if (failed) this.refreshFromCart();
          }
        }

        async removeLine(key) {
          const response = await fetch(
            `${(window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change'}.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(this.withSections({ id: key, quantity: 0 })),
            }
          );

          if (!response.ok) return null;
          return response.json();
        }

        async removeUpsell(input) {
          this.setCardLoading(input, true);
          this.trackEngagement('upsell_remove', { upsell_product: input.dataset.productHandle, upsell_parent: this.dataset.productHandle });
          // Arm before the network call: the flyout must never appear on remove.
          const suppressor = this.smartCartVisibleEl() ? null : this.suppressSmartCartOpen(1500);
          let failed = false;

          try {
            const cachedKey = input.dataset.lineKey;
            let result = cachedKey ? await this.removeLine(cachedKey) : null;

            if (!result) {
              const line = this.findCartLine(await this.fetchCart(), input);
              result = line ? await this.removeLine(line.key) : null;
            }

            delete input.dataset.lineKey;

            // Nothing left in the cart to remove — the card is simply unticked.
            if (!result) return;

            this.updateCartSections(result);
            this.notifyCartUpdated();
          } catch (err) {
            console.error('[product-upsell] Failed to remove upsell item', err);
            failed = true;
            input.checked = true;
          } finally {
            this.setCardLoading(input, false);
            // The network round-trip may have consumed most of the initial
            // window (slow connections), and Rebuy reacts to the RESPONSE, so
            // keep suppressing for a beat after completion.
            if (suppressor) suppressor.extend(2500);
            if (failed) this.refreshFromCart();
          }
        }

        smartCartVisibleEl() {
          return document.querySelector('.rebuy-cart.is-visible, .rebuy-cart__flyout.is-visible');
        }

        openSmartCart() {
          try {
            if (window.CINativeCart && typeof window.CINativeCart.open === 'function') {
              window.CINativeCart.open();
            } else if (window.Rebuy && Rebuy.SmartCart && typeof Rebuy.SmartCart.show === 'function') {
              Rebuy.SmartCart.show();
            }
          } catch (err) { /* cart UI opening is best-effort */ }
        }

        // Removing a line is a retreat, not a purchase step, so the flyout must
        // not appear. Rebuy opens it on ANY /cart/change.js it observes (verified
        // with a raw fetch, no widget involved), so prevention is impossible from
        // our side; instead revert the open BEFORE paint. The observer fires in
        // the microtask between Rebuy's class flip and the next frame, so no open
        // state is ever rendered. Guarded write: hide() only acts while the
        // visible class is present, and removing it ends the loop, so the
        // observer cannot feed itself. Timeout backstops cover a flyout node
        // Rebuy re-creates instead of re-classing; everything disarms after the
        // window.
        suppressSmartCartOpen(initialMs) {
          const state = { until: Date.now() + initialMs };
          const hide = () => {
            try {
              if (this.smartCartVisibleEl() && window.Rebuy && Rebuy.SmartCart && typeof Rebuy.SmartCart.hide === 'function') {
                Rebuy.SmartCart.hide();
                // Rebuy tried to open inside the window: assume it may retry
                // (section fetches, queued events) and stay armed a bit longer.
                state.until = Math.max(state.until, Date.now() + 1000);
              }
            } catch (err) { /* best-effort */ }
          };
          const observer = new MutationObserver(hide);
          observer.observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true, childList: true });
          const ticker = setInterval(() => {
            if (Date.now() >= state.until) {
              observer.disconnect();
              clearInterval(ticker);
              return;
            }
            hide(); // backstop for anything the observer missed
          }, 250);
          return {
            extend: (ms) => { state.until = Math.max(state.until, Date.now() + ms); },
          };
        }

        findCartLine(cart, input) {
          const items = (cart && cart.items) || [];
          const variantId = String(input.value);
          const matches = items.filter((item) => String(item.variant_id) === variantId);
          if (!matches.length) return null;
          // Prefer a line this component added, so we don't remove a line the
          // customer built somewhere else with different properties.
          return matches.find((item) => (item.properties || {})._upsell_parent) || matches[0];
        }

        applyCartState(cart) {
          this.querySelectorAll('.product-upsell__checkbox').forEach((input) => {
            const line = this.findCartLine(cart, input);
            input.checked = Boolean(line);
            if (line) {
              input.dataset.lineKey = line.key;
            } else {
              delete input.dataset.lineKey;
            }
          });
        }

        async refreshFromCart(cart) {
          if (this.pending) return;
          try {
            let data = cart || (await this.fetchCart());
            this.rememberSeenParents(data);

            if (this.discountEnabled) {
              // Reconciling rewrites line properties, which mints new line keys,
              // so read the cart state back from whatever it leaves behind.
              const reconciled = await this.reconcileOrphanedUpsells(data);
              if (reconciled) data = reconciled;
            }

            this.applyCartState(data);
          } catch (err) {
            console.error('[product-upsell] Failed to read cart', err);
          }
        }

        get reconcileLock() { return this._reconcileLock; }
        set reconcileLock(v) { this._reconcileLock = v; }

        async findExistingLine(variantId) {
          const cart = await this.fetchCart();
          if (!cart || !Array.isArray(cart.items)) return null;
          return cart.items.find((i) => i.variant_id === variantId && i.quantity > 0) || null;
        }

        settleOptimistic(optimistic, cartState) {
          try {
            if (optimistic && typeof optimistic.settle === 'function') optimistic.settle();
          } catch (e) {}
          try {
            document.dispatchEvent(new CustomEvent('ci:cart:rerendered'));
            document.dispatchEvent(new CustomEvent('rebuy:cart.change'));
          } catch (e) {}
          try { this.refreshFromCart(cartState); } catch (e) {}
          try { this.setCardLoading(null, false); } catch (e) {}
        }

        async fetchCart() {
          return fetch(
            `${(window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart'}.js`,
            { headers: { Accept: 'application/json' } }
          ).then((r) => r.json());
        }

        presentParentIds(items) {
          const ids = new Set();
          items.forEach((item) => {
            ids.add(String(item.product_id));
            ids.add(String(item.variant_id));
          });
          return ids;
        }

        readSeenParents() {
          try {
            const raw = window.localStorage.getItem(SEEN_PARENTS_KEY);
            const list = raw ? JSON.parse(raw) : [];
            return Array.isArray(list) ? list.map(String) : [];
          } catch (err) {
            return [];
          }
        }

        rememberSeenParents(cart) {
          const items = (cart && cart.items) || [];
          if (!items.length) return;

          const present = this.presentParentIds(items);
          const seen = this.readSeenParents();
          let changed = false;

          items.forEach((item) => {
            const parent = (item.properties || {})._upsell_parent;
            if (!parent) return;
            const id = String(parent);
            if (!present.has(id) || seen.includes(id)) return;
            seen.push(id);
            changed = true;
          });

          if (!changed) return;
          try {
            window.localStorage.setItem(SEEN_PARENTS_KEY, JSON.stringify(seen.slice(-20)));
          } catch (err) {
            /* storage unavailable — the discount simply stays put */
          }
        }

        findOrphanedUpsell(cart) {
          if (!cart || !Array.isArray(cart.items)) return null;
          const present = this.presentParentIds(cart.items);
          const seen = this.readSeenParents();
          return cart.items.find((item) => {
            const props = item.properties || {};
            const parent = props._upsell_parent ? String(props._upsell_parent) : '';
            return parent && props._upsell_price && !present.has(parent) && seen.includes(parent);
          }) || null;
        }

        async reconcileOrphanedUpsells(initialCart) {
          if (this.reconcileLock) return null;
          this.reconcileLock = true;
          try {
            let changed = false;
            let cart = initialCart || (await this.fetchCart());
            let guard = 0;
            let orphan = this.findOrphanedUpsell(cart);

            while (orphan && guard < 50) {
              guard += 1;
              const cleaned = { ...(orphan.properties || {}) };
              cleaned._upsell_price = '';

              const res = await fetch(
                `${(window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change'}.js`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                  },
                  body: JSON.stringify({ id: orphan.key, properties: cleaned }),
                }
              );

              if (!res.ok) break;
              changed = true;
              cart = await res.json();
              orphan = this.findOrphanedUpsell(cart);
            }

            if (!changed) return null;

            const sections = this.cartSectionIds();
            if (sections.length) {
              cart = await fetch(
                `${(window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart'}.js?sections=${sections.join(',')}`,
                { headers: { Accept: 'application/json' } }
              ).then((r) => r.json());
              this.updateCartSections(cart);
            }
            this.notifyCartUpdated();
            return cart;
          } catch (err) {
            console.error('[product-upsell] Failed to reconcile orphaned upsells', err);
            return null;
          } finally {
            this.reconcileLock = false;
          }
        }

        // --- Recommended second slot ------------------------------------
        // Rebuy chooses the product because it is trained on co-purchase, not
        // similarity; Shopify's own `related` intent returns substitutes, which
        // would put a competing bracelet under "Complete the ritual".
        // The card itself is rendered by Liquid via the Section Rendering API,
        // so the recommendation keeps its image strip and More-info drawer.
        async waitForRebuy() {
          return null; // Rebuy is gone from this theme (2026-09-02)
        }

        excludedHandles() {
          const set = new Set();
          if (this.dataset.productHandle) set.add(this.dataset.productHandle);
          this.querySelectorAll('.product-upsell__item:not([data-upsell-dynamic-slot]) .product-upsell__checkbox')
            .forEach((input) => {
              if (input.dataset.productHandle) set.add(input.dataset.productHandle);
            });
          return set;
        }

        // Every path out of the fetch has to settle the empty slots, or a
        // failed/short recommendation pass leaves blank cards padding the row.
        async loadRecommendation() {
          try {
            await this.fetchRecommendations();
          } finally {
            this.settlePlaceholders();
          }
        }

        // A slot that got a card is revealed; one that stayed empty is removed.
        // In the stacked arm nothing fills them, so they all go and the widget
        // renders exactly as it does today.
        settlePlaceholders() {
          this.querySelectorAll('[data-upsell-placeholder]').forEach((slot) => {
            if (slot.dataset.upsellRecommended) {
              slot.hidden = false;
            } else {
              slot.remove();
            }
          });
          if (typeof this._updateRowNav === 'function') this._updateRowNav();
          // Reveal the settled slots (see .product-upsell--recs-pending in the snippet).
          this.classList.remove('product-upsell--recs-pending');
        }

        async fetchRecommendations() {
          const dynamicSlots = Array.from(this.querySelectorAll('[data-upsell-dynamic-slot]'));
          const slot2 = dynamicSlots[0];
          if (!slot2) return;

          // Test-arm switch: Intelligems differentiates variants with one CSS line.
          //   pinned:  (default) slot 1 is the configured product
          //   all:     product-upsell.pdp-product-upsell { --upsell-mode: all; }
          const mode = this.recMode();
          const slot1 = this.querySelector('[data-upsell-slot="1"]');
          // Stacked arm behaves exactly as before: one dynamic slot. The slider
          // arm asks for every slot the row has room for.
          const dynamic = this.isRowLayout() ? dynamicSlots : [slot2];
          const slots = (mode === 'all' && slot1) ? [slot1, ...dynamic] : dynamic;

          const productId = this.dataset.productId;
          if (!productId) return;

          // Pinned mode keeps every static slot's product out of the pool; in
          // all mode only the host product is off-limits, so the handbook can
          // win a slot on merit if the engine ranks it.
          const excluded = mode === 'all'
            ? new Set([this.dataset.productHandle])
            : this.excludedHandles();
          const need = slots.length;

          let handles = [];
          try {
            // 2026-09-02 (Ondrej: no Rebuy, no third-party dependency, no
            // commissions). Shopify's native engine, COMPLEMENTARY intent first:
            // that list is fed by our own co-purchase engine (orders -> pair
            // counts -> Shopify's complementary-products metafield), so the
            // picks are add-ons, not substitutes. `related` (similarity) tops
            // the pool up when complements alone cannot fill every slot.
            //
            // 2026-09-17 (Ondrej): every slot must fill, the row arm always
            // shows five. So the price cap is tiered rather than all-or-nothing:
            // slots draw from picks priced <= the host first, then <= 1.5x the
            // host, then the rest. A host with enough cheap complements never
            // sees tier 2; a cheap host widens exactly as far as it has to.
            const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
            const hostPrice = parseFloat(this.dataset.productPrice || '0') || 0;
            const pull = async (intent) => {
              const res = await fetch(root + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=' + (this.isRowLayout() ? 16 : 10) + '&intent=' + intent, { headers: { Accept: 'application/json' } });
              if (!res.ok) return [];
              const json = await res.json();
              return (json.products || []).filter((item) => item && item.handle && item.available !== false);
            };
            const usable = (list) => list.filter((it) => !excluded.has(it.handle)).length;

            this._recIntents = {};
            let pool = await pull('complementary');
            pool.forEach((it) => { this._recIntents[it.handle] = 'complementary'; });
            if (usable(pool) < need) {
              const seen = new Set(pool.map((it) => it.handle));
              (await pull('related')).forEach((it) => {
                if (seen.has(it.handle)) return;
                seen.add(it.handle);
                this._recIntents[it.handle] = 'related';
                pool.push(it);
              });
            }
            this._recIntent = pool.some((it) => this._recIntents[it.handle] === 'complementary') ? 'complementary' : 'related';
            this._recRanks = {};
            pool.forEach((it, i) => { this._recRanks[it.handle] = i; this._recRanks[String(it.id)] = i; });

            this._recTiers = {};
            const tierOf = (it) => {
              if (!hostPrice) return 1;
              const price = it.price / 100;
              if (price <= hostPrice) return 1;
              if (price <= hostPrice * 1.5) return 2;
              return 3;
            };
            pool.forEach((it) => { this._recTiers[it.handle] = tierOf(it); });
            // Stable sort by tier; rank order is preserved within a tier.
            pool = pool.map((it, i) => ({ it, i })).sort((a, b) => (tierOf(a.it) - tierOf(b.it)) || (a.i - b.i)).map((x) => x.it);
            handles = pool.map((item) => item.handle);
          } catch (err) {
            return; // fallback cards stay exactly as rendered
          }

          let changed = false;
          for (const slot of slots) {
            const pick = handles.find((handle) => !excluded.has(handle));
            if (!pick) continue;
            excluded.add(pick);
            if (await this.fillSlot(slot, pick)) {
              changed = true;
              this.trackEngagement('ci_rec_impression', { ci_surface: 'pdp', ci_host: String(productId), ci_candidate: pick,
                ci_rank: String((this._recRanks && this._recRanks[pick]) ?? ''), ci_pos: slots.indexOf(slot) + (slots[0] === slot1 ? 1 : 2),
                ci_intent: (this._recIntents && this._recIntents[pick]) || this._recIntent || '', ci_tier: String((this._recTiers && this._recTiers[pick]) || '') });
            }
          }
          if (changed) this.refreshFromCart();
        }

        async fillSlot(slot, pick) {
          try {
            const section = this.dataset.recommendSection || 'upsell-card';
            const res = await fetch('/products/' + encodeURIComponent(pick) + '?section_id=' + encodeURIComponent(section), {
              headers: { Accept: 'text/html' },
            });
            if (!res.ok) return false;
            const html = (await res.text()).trim();
            if (!html) return false;
            const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-upsell__card');
            if (!card) return false;
            slot.replaceChildren(document.importNode(card, true));
            slot.dataset.upsellRecommended = pick;
            return true;
          } catch (err) {
            return false; // fallback card stays exactly as rendered
          }
        }

        buildInfoDots() {
          const strip = this.infoSlot.querySelector('.product-upsell-info__images');
          if (!strip) return;
          const imgs = Array.from(strip.querySelectorAll('.product-upsell-info__image'));
          if (imgs.length < 2) return;

          const dots = document.createElement('div');
          dots.className = 'product-upsell-info__dots';
          imgs.forEach((img, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'product-upsell-info__dot';
            dot.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + imgs.length);
            dot.addEventListener('click', () => {
              strip.scrollTo({ left: i * (imgs[0].offsetWidth + 8), behavior: 'smooth' });
            });
            dots.appendChild(dot);
          });
          strip.after(dots);

          const update = () => {
            const step = imgs[0].offsetWidth + 8;
            const idx = Math.min(imgs.length - 1, Math.max(0, Math.round(strip.scrollLeft / step)));
            dots.querySelectorAll('.product-upsell-info__dot').forEach((d, i) => {
              d.classList.toggle('is-active', i === idx);
            });
          };
          strip.addEventListener('scroll', update, { passive: true });
          update();

          // Desktop: arrow buttons over the gallery (touch devices swipe).
          if (!window.matchMedia('(hover: none)').matches) {
            const wrap = document.createElement('div');
            wrap.className = 'product-upsell-info__gallery';
            strip.parentNode.insertBefore(wrap, strip);
            wrap.appendChild(strip);
            const makeArrow = (dir) => {
              const b = document.createElement('button');
              b.type = 'button';
              b.className = 'product-upsell-info__nav product-upsell-info__nav--' + (dir < 0 ? 'prev' : 'next');
              b.setAttribute('aria-label', dir < 0 ? 'Previous photo' : 'Next photo');
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('viewBox', '0 0 8 12');
              svg.setAttribute('aria-hidden', 'true');
              const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
              path.setAttribute('d', dir < 0 ? 'M7 1L1.5 6 7 11' : 'M1 1l5.5 5L1 11');
              path.setAttribute('fill', 'none');
              path.setAttribute('stroke', 'currentColor');
              path.setAttribute('stroke-width', '1.8');
              path.setAttribute('stroke-linecap', 'round');
              path.setAttribute('stroke-linejoin', 'round');
              svg.appendChild(path);
              b.appendChild(svg);
              b.addEventListener('click', () => {
                strip.scrollBy({ left: dir * (imgs[0].offsetWidth + 8), behavior: 'smooth' });
              });
              wrap.appendChild(b);
              return b;
            };
            const prev = makeArrow(-1);
            const next = makeArrow(1);
            const syncArrows = () => {
              prev.classList.toggle('is-hidden', strip.scrollLeft < 10);
              next.classList.toggle('is-hidden', strip.scrollLeft > strip.scrollWidth - strip.clientWidth - 10);
            };
            strip.addEventListener('scroll', syncArrows, { passive: true });
            syncArrows();
          }
        }

        // Reviews load only when a drawer opens (never on page load), one small
        // request per product per session, cached after that. Rows are built
        // with createElement/textContent because review text is customer input.
        async loadInfoReviews() {
          const holder = this.infoSlot.querySelector('[data-upsell-reviews]');
          if (!holder) return;
          const okeId = holder.dataset.okeProductId;
          this._okendoCache = this._okendoCache || new Map();

          let reviews = this._okendoCache.get(okeId);
          if (!reviews) {
            try {
              const res = await fetch(
                'https://api.okendo.io/v1/stores/' + OKENDO_STORE_ID + '/products/' + encodeURIComponent(okeId) + '/reviews?limit=3&orderBy=date%20desc',
                { headers: { Accept: 'application/json' } }
              );
              if (!res.ok) return;
              reviews = ((await res.json()).reviews || []).filter((r) => !r.status || r.status === 'approved');
              this._okendoCache.set(okeId, reviews);
            } catch (err) {
              return; // drawer simply shows no reviews section
            }
          }
          if (!reviews.length || !holder.isConnected) return;

          holder.replaceChildren();
          const heading = document.createElement('p');
          heading.className = 'product-upsell-info__reviews-heading';
          heading.textContent = 'Recent reviews';
          holder.appendChild(heading);

          reviews.forEach((rv) => {
            const row = document.createElement('div');
            row.className = 'product-upsell-info__review';

            const stars = document.createElement('div');
            stars.className = 'product-upsell-info__review-stars';
            const rating = Math.max(0, Math.min(5, Math.round(rv.rating || 0)));
            for (let i = 0; i < 5; i++) {
              const st = document.createElement('span');
              st.textContent = '\u2605';
              if (i >= rating) st.className = 'star-empty';
              stars.appendChild(st);
            }
            row.appendChild(stars);

            const meta = document.createElement('p');
            meta.className = 'product-upsell-info__review-meta';
            const name = document.createElement('strong');
            name.textContent = (rv.reviewer && rv.reviewer.displayName) || 'Anonymous';
            meta.appendChild(name);
            if (rv.reviewer && rv.reviewer.isVerified) meta.appendChild(document.createTextNode(' \u00b7 Verified buyer'));
            row.appendChild(meta);

            if (rv.title) {
              const title = document.createElement('p');
              title.className = 'product-upsell-info__review-title';
              title.textContent = rv.title.trim();
              row.appendChild(title);
            }
            if (rv.body) {
              const body = document.createElement('p');
              body.className = 'product-upsell-info__review-body';
              body.textContent = rv.body.trim();
              row.appendChild(body);
            }
            holder.appendChild(row);
          });

          // Total count is already in the drawer's star snippet; make clear
          // these three are a sample, not the whole book.
          const countEl = this.infoSlot.querySelector('.oke-sr-count-number');
          const total = countEl ? parseInt(countEl.textContent, 10) : NaN;
          if (!Number.isNaN(total) && total > reviews.length) {
            const more = document.createElement('p');
            more.className = 'product-upsell-info__reviews-more';
            more.textContent = 'and ' + (total - reviews.length) + ' more reviews';
            holder.appendChild(more);
          }
        }

        // Test-arm switch, same pattern as --upsell-mode:
        //   portrait row: product-upsell.pdp-product-upsell { --upsell-layout: slider; }
        //   wide row:     product-upsell.pdp-product-upsell { --upsell-layout: strip; }
        //   stacked:      (default, no variable needed)
        // ?upsell_layout=slider|strip|stack overrides it for manual preview
        // review; the real test drives it from the Intelligems CSS line above.
        layoutMode() {
          const ARMS = ['slider', 'strip', 'stack'];
          let override = '';
          try {
            override = (new URLSearchParams(window.location.search).get('upsell_layout') || '').trim();
          } catch (err) { /* no URLSearchParams: fall through to CSS */ }
          if (ARMS.indexOf(override) !== -1) return override;
          const css = (getComputedStyle(this).getPropertyValue('--upsell-layout') || '').replace(/["']/g, '').trim();
          return ARMS.indexOf(css) !== -1 ? css : 'stack';
        }

        // Slot-1 switch, same idiom: --upsell-mode: all unpins the handbook so
        // every slot is a recommendation. Returns 'all' or '' (pinned).
        // 'all-rebuy' is the pre-2026-09 name from the Rebuy era and is kept
        // as an alias so an experience still using it keeps working.
        // ?upsell_mode=all|pinned overrides it for manual preview review only.
        recMode() {
          let override = '';
          try {
            override = (new URLSearchParams(window.location.search).get('upsell_mode') || '').trim();
          } catch (err) { /* fall through to CSS */ }
          if (override === 'all') return 'all';
          if (override === 'pinned') return '';
          const css = (getComputedStyle(this).getPropertyValue('--upsell-mode') || '').replace(/["']/g, '').trim();
          return (css === 'all' || css === 'all-rebuy') ? 'all' : '';
        }

        isRowLayout() {
          return this._layout === 'slider' || this._layout === 'strip';
        }

        applyLayoutMode() {
          this._layout = this.layoutMode();
          this.classList.toggle('product-upsell--row', this.isRowLayout());
          // The row arm also tightens the Shop Pay installments line above the
          // widget (a sibling, so it needs a hook outside this element).
          document.documentElement.classList.toggle('ci-upsell-row', this.isRowLayout());
          this.classList.toggle('product-upsell--slider', this._layout === 'slider');
          this.classList.toggle('product-upsell--strip', this._layout === 'strip');
        }

        // Test-arm switch, same pattern as --upsell-mode:
        //   tap-to-add:  product-upsell.pdp-product-upsell { --upsell-tap: add; }
        //   tap-to-info: (default, no variable needed)
        tapMode() {
          const css = (getComputedStyle(this).getPropertyValue('--upsell-tap') || '').replace(/["']/g, '').trim();
          return css || 'info';
        }

        bindDescToggle() {
          const btn = this.infoSlot.querySelector('[data-upsell-desc-more]');
          if (!btn) return;
          const body = this.infoSlot.querySelector('.product-upsell-info__body');
          if (!body) return;

          // Short description that fits the teaser anyway: no clamp, no button.
          setTimeout(() => {
            if (body.scrollHeight - body.clientHeight < 24) {
              body.classList.remove('is-clamped');
              btn.remove();
            }
          }, 60);

          btn.addEventListener('click', () => {
            const expanded = !body.classList.contains('is-clamped');
            body.classList.toggle('is-clamped', expanded);
            btn.textContent = expanded ? 'Read more' : 'Read less';
            btn.setAttribute('aria-expanded', String(!expanded));
            this.syncAtcFloat(); // content height changed under the floating ATC
          });
        }

        // Tapping the stars under the title jumps to the reviews further down.
        bindRatingJump() {
          const rating = this.infoSlot.querySelector('.product-upsell-info__rating');
          const target = this.infoSlot.querySelector('[data-upsell-reviews]');
          if (!rating || !target) return;
          rating.setAttribute('role', 'button');
          rating.setAttribute('tabindex', '0');
          rating.setAttribute('aria-label', 'Jump to reviews');
          const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const jump = () => {
            this.infoSlot.scrollTo({
              top: target.offsetTop - this.infoSlot.offsetTop - 8,
              behavior: reduced ? 'auto' : 'smooth',
            });
          };
          rating.addEventListener('click', jump);
          rating.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              jump();
            }
          });
        }

        // The primary ATC sits inline right under the benefits; the footer
        // copy floats in only after the inline one scrolls out of view.
        buildInlineAtc() {
          const anchor = this.infoSlot.querySelector('.product-upsell-info__benefits')
            || this.infoSlot.querySelector('.product-upsell-info__dots')
            || this.infoSlot.querySelector('.product-upsell-info__images');
          if (!anchor) return;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'product-upsell-info__atc product-upsell-info__atc--inline';
          btn.setAttribute('data-upsell-info-atc-inline', '');
          btn.addEventListener('click', this.handleInfoAtc);
          anchor.after(btn);
        }

        infoAtcButtons() {
          const buttons = [];
          if (this.infoAtc) buttons.push(this.infoAtc);
          const inline = this.infoSlot ? this.infoSlot.querySelector('[data-upsell-info-atc-inline]') : null;
          if (inline) buttons.push(inline);
          return buttons;
        }

        updateInfoAtc() {
          const cb = this.infoCheckbox;
          this.infoAtcButtons().forEach((btn) => {
            if (!cb) { btn.hidden = true; return; }
            btn.hidden = false;
            if (cb.checked) {
              btn.textContent = 'Added. Tap to remove';
              btn.classList.add('is-added');
            } else {
              const priceEl = cb.closest('.product-upsell__card').querySelector('[data-upsell-price]');
              const price = priceEl ? priceEl.textContent.trim() : '';
              btn.textContent = price ? 'Add to cart · ' + price : 'Add to cart';
              btn.classList.remove('is-added');
            }
          });
        }

        syncAtcFloat() {
          const footer = this.infoDrawer ? this.infoDrawer.querySelector('.product-upsell-info__footer') : null;
          if (!footer) return;
          const inline = this.infoSlot.querySelector('[data-upsell-info-atc-inline]');
          if (!inline) { footer.classList.remove('is-collapsed'); return; }
          const slotTop = this.infoSlot.getBoundingClientRect().top;
          const inlineVisible = inline.getBoundingClientRect().bottom > slotTop;
          footer.classList.toggle('is-collapsed', inlineVisible);
        }

        handleInfoAtc(event) {
          if (!this.infoCheckbox) return;
          // Record which surface triggered the add before proxying the click;
          // buildLineItem consumes this synchronously on the same call stack.
          const btn = event && event.currentTarget;
          this._pendingAddSource = btn && btn.classList.contains('product-upsell-info__atc--inline')
            ? 'drawer-inline'
            : 'drawer-floating';
          // Proxy the card's checkbox so there is exactly one add/remove path
          // (line keys, Rebuy sync and cart seeding all live on that path).
          this.infoCheckbox.click();
          this.closeInfo();
        }

        // Touch-only fullscreen image view. Tapping a drawer photo opens it;
        // tap again to zoom and pan by scrolling. Deliberately NOT a pinch
        // handler: pinch inside a fixed sheet zooms the visual viewport on iOS
        // and leaves the sheet pinned, which reads as broken.
        // Fullscreen image viewer with REAL pinch-zoom, implemented as a CSS
        // transform. Native pinch is suppressed on purpose: it zooms the visual
        // viewport, which makes Safari re-rasterize this fixed fullscreen layer
        // on top of a heavy PDP and kills the web process (iPhone, 2026-08-11).
        bindZoom() {
          this.zoom = this.querySelector('[data-upsell-zoom]');
          if (!this.zoom) return;
          if (this.zoom.parentElement !== document.body) document.body.appendChild(this.zoom);

          this.zoomImage = this.zoom.querySelector('[data-upsell-zoom-image]');
          this.zoomStage = this.zoom.querySelector('[data-upsell-zoom-scroll]');

          const hint = this.zoom.querySelector('[data-upsell-zoom-hint]');
          if (hint) {
            hint.textContent = window.matchMedia('(hover: none)').matches
              ? 'Pinch or double-tap to zoom'
              : 'Click image to zoom, drag to pan';
          }

          this.zoomState = { scale: 1, x: 0, y: 0 };

          this.infoSlot.addEventListener('click', (event) => {
            const img = event.target.closest('.product-upsell-info__image');
            if (!img) return;
            event.preventDefault();
            this.openZoom(img.currentSrc || img.src, img.alt);
          });

          const close = () => this.closeZoom();
          this.zoom.querySelector('[data-upsell-zoom-close]').addEventListener('click', close);
          this.handleZoomKeydown = (event) => { if (event.key === 'Escape') close(); };

          // Suppress WebKit's own pinch so the visual viewport never scales.
          ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
            this.zoom.addEventListener(type, (event) => event.preventDefault());
          });

          const pointers = new Map();
          let startDist = 0;
          let startScale = 1;
          let startMid = { x: 0, y: 0 };
          let startOffset = { x: 0, y: 0 };
          let movedDistance = 0;
          let lastTap = 0;

          const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
          const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

          const apply = () => {
            const st = this.zoomState;
            this.zoomImage.style.transform =
              'translate(' + st.x + 'px, ' + st.y + 'px) scale(' + st.scale + ')';
            this.zoom.classList.toggle('is-zoomed', st.scale > 1.02);
          };
          this.applyZoomTransform = apply;

          // Keep the image from being dragged off screen.
          const clamp = () => {
            const st = this.zoomState;
            const r = this.zoomImage.getBoundingClientRect();
            const scaledW = (r.width / (st.scale || 1)) * st.scale;
            const scaledH = (r.height / (st.scale || 1)) * st.scale;
            const maxX = Math.max(0, (scaledW - window.innerWidth) / 2);
            const maxY = Math.max(0, (scaledH - window.innerHeight) / 2);
            st.x = Math.min(maxX, Math.max(-maxX, st.x));
            st.y = Math.min(maxY, Math.max(-maxY, st.y));
          };
          this.clampZoom = clamp;

          const onDown = (event) => {
            this.zoom.classList.remove('is-animating');
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            movedDistance = 0;
            if (pointers.size === 2) {
              const [a, b] = [...pointers.values()];
              startDist = dist(a, b);
              startScale = this.zoomState.scale;
              startMid = mid(a, b);
              startOffset = { x: this.zoomState.x, y: this.zoomState.y };
            } else if (pointers.size === 1) {
              startMid = { x: event.clientX, y: event.clientY };
              startOffset = { x: this.zoomState.x, y: this.zoomState.y };
            }
            if (this.zoomStage.setPointerCapture) {
              try { this.zoomStage.setPointerCapture(event.pointerId); } catch (err) { /* ignore */ }
            }
          };

          const onMove = (event) => {
            if (!pointers.has(event.pointerId)) return;
            event.preventDefault();
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

            if (pointers.size === 2) {
              const [a, b] = [...pointers.values()];
              const d = dist(a, b);
              if (startDist > 0) {
                const next = Math.min(4, Math.max(1, startScale * (d / startDist)));
                const m = mid(a, b);
                this.zoomState.scale = next;
                this.zoomState.x = startOffset.x + (m.x - startMid.x);
                this.zoomState.y = startOffset.y + (m.y - startMid.y);
                clamp();
                apply();
              }
            } else if (pointers.size === 1 && this.zoomState.scale > 1.02) {
              const p = [...pointers.values()][0];
              movedDistance = Math.hypot(p.x - startMid.x, p.y - startMid.y);
              this.zoomState.x = startOffset.x + (p.x - startMid.x);
              this.zoomState.y = startOffset.y + (p.y - startMid.y);
              clamp();
              apply();
            }
          };

          const reset = (to) => {
            this.zoom.classList.add('is-animating');
            this.zoomState.scale = to;
            this.zoomState.x = 0;
            this.zoomState.y = 0;
            apply();
            setTimeout(() => this.zoom.classList.remove('is-animating'), 260);
          };
          this.resetZoom = reset;

          const onUp = (event) => {
            pointers.delete(event.pointerId);
            if (pointers.size > 0) return;

            // A tap (not a drag): double-tap toggles, single tap on the
            // backdrop closes.
            if (movedDistance < 8) {
              const onImage = event.target === this.zoomImage;
              const now = Date.now();
              if (onImage && now - lastTap < 320) {
                reset(this.zoomState.scale > 1.02 ? 1 : 2.5);
                lastTap = 0;
                return;
              }
              lastTap = onImage ? now : 0;
              if (!onImage) { close(); return; }
              // Pointer devices have no pinch: a single click steps the zoom.
              if (!window.matchMedia('(hover: none)').matches) {
                reset(this.zoomState.scale > 1.02 ? 1 : 2);
              }
            }
          };

          this.zoomStage.addEventListener('pointerdown', onDown);
          this.zoomStage.addEventListener('pointermove', onMove, { passive: false });
          this.zoomStage.addEventListener('pointerup', onUp);
          this.zoomStage.addEventListener('pointercancel', (e) => pointers.delete(e.pointerId));
        }

        openZoom(src, alt) {
          if (!this.zoom) return;
          this.zoomImage.src = src;
          this.zoomImage.alt = alt || '';
          this.zoomState = { scale: 1, x: 0, y: 0 };
          this.zoom.classList.remove('is-zoomed', 'is-animating');
          this.zoomImage.style.transform = '';
          this.zoom.hidden = false;
          document.body.classList.add('overflow-hidden');
          document.addEventListener('keydown', this.handleZoomKeydown);
        }

        closeZoom() {
          if (!this.zoom || this.zoom.hidden) return;
          this.zoom.hidden = true;
          this.zoom.classList.remove('is-zoomed', 'is-animating');
          this.zoomImage.style.transform = '';
          // Drop the decoded bitmap: these are full-size product shots and the
          // PDP is already memory-heavy on phones.
          this.zoomImage.removeAttribute('src');
          this.zoomState = { scale: 1, x: 0, y: 0 };
          document.removeEventListener('keydown', this.handleZoomKeydown);
          if (!this.infoDrawer || this.infoDrawer.hidden) document.body.classList.remove('overflow-hidden');
        }

        // Engagement beacons. Convert used to count every checkbox tick; the
        // order marker only counts adds that survive to purchase. These restore
        // the funnel in between: widget seen -> drawer opened -> ticked.
        // Sinks: dataLayer (GTM/GA4) and Microsoft Clarity custom events, both
        // already on the page, both guarded so their absence never breaks adds.
        trackEngagement(name, detail) {
          // 2026-09-02: route through the shared tracker (GA4 + dataLayer + Clarity) when present
          if (typeof window.__ciTrack === 'function') { try { window.__ciTrack(name, detail || {}); } catch (err) {} return; }
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(Object.assign({ event: name }, detail || {}));
          } catch (err) { /* never let analytics break the widget */ }
          try {
            if (typeof window.clarity === 'function') window.clarity('event', name);
          } catch (err) { /* ditto */ }
        }

        observeImpression() {
          if (this._impressionObserved || typeof IntersectionObserver !== 'function') return;
          this._impressionObserved = true;
          const io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (getComputedStyle(this).display === 'none') return; // control arm
              io.disconnect();
              this.trackEngagement('upsell_widget_view', { upsell_tap_mode: this.tapMode(), upsell_parent: this.dataset.productHandle });
            });
          }, { threshold: 0.5 });
          io.observe(this);
        }

        trackConversion(input) {
          const goalId = this.convGoalId;
          if (!goalId || goalId === 'REPLACE_WITH_GOAL_ID') return;
          const key = input.value;
          if (this.trackedConversions.has(key)) return;
          this.trackedConversions.add(key);

          window._conv_q = window._conv_q || [];
          window._conv_q.push(['triggerConversion', goalId]);
        }

        bindInfoDrawer() {
          this.infoDrawer = this.querySelector('[data-upsell-info-drawer]');
          if (!this.infoDrawer) return;
          if (this.infoDrawer.parentElement !== document.body) {
            document.body.appendChild(this.infoDrawer);
          }

          this.infoSlot = this.infoDrawer.querySelector('[data-upsell-info-slot]');
          this.infoPanel = this.infoDrawer.querySelector('.product-upsell-info__panel');
          this.infoAtc = this.infoDrawer.querySelector('[data-upsell-info-atc]');
          if (this.infoAtc) this.infoAtc.addEventListener('click', this.handleInfoAtc);
          this.bindZoom();

          this.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-upsell-info-trigger]');
            if (trigger && this.contains(trigger)) {
              // The trigger sits inside the card's <label>, so without this the
              // click would also toggle the checkbox and add to cart.
              event.preventDefault();
              event.stopPropagation();
              this.openInfo(trigger.dataset.infoTarget, trigger);
              return;
            }

            // Tapping the product itself (image, name, price) means "tell me
            // more", not "buy it": open the drawer. Only the +/check circle
            // and the drawer's own button add or remove.
            const zone = event.target.closest('.product-upsell__image, .product-upsell__title, .product-upsell__price-group');
            if (!zone || !this.contains(zone)) return;
            if (this.tapMode() === 'add') return; // arm: whole card adds (default label behavior)
            const card = zone.closest('.product-upsell__card');
            const cardTrigger = card && card.querySelector('[data-upsell-info-trigger]');
            if (!cardTrigger) return; // no description: keep the default add
            event.preventDefault();
            event.stopPropagation();
            this.openInfo(cardTrigger.dataset.infoTarget, cardTrigger);
          });

          this.infoDrawer
            .querySelector('[data-upsell-info-overlay]')
            .addEventListener('click', this.closeInfo);
          this.infoDrawer
            .querySelector('[data-upsell-info-close]')
            .addEventListener('click', this.closeInfo);
        }

        handleInfoTrigger(event) {
          event.preventDefault();
          event.stopPropagation();
          const targetId = event.currentTarget.dataset.infoTarget;
          this.openInfo(targetId, event.currentTarget);
        }

        openInfo(targetId, trigger) {
          if (!this.infoDrawer || !this.infoSlot) return;

          const template = this.querySelector(`[data-upsell-info-content="${targetId}"]`);
          if (!template) return;

          // Header shows the product name, so the drawer is self-identifying.
          const titleEl = this.infoDrawer.querySelector('.product-upsell-info__title');
          const label = trigger && trigger.dataset ? trigger.dataset.infoTitle : '';
          if (titleEl && label) titleEl.textContent = label;

          this.infoSlot.replaceChildren(template.content.cloneNode(true));
          this.infoSlot.scrollTop = 0;
          this.infoTrigger = trigger || null;

          const card = trigger ? trigger.closest('.product-upsell__card') : null;
          this.infoCheckbox = card ? card.querySelector('.product-upsell__checkbox') : null;
          this.trackEngagement('upsell_drawer_open', {
            upsell_product: this.infoCheckbox ? this.infoCheckbox.dataset.productHandle : null,
            upsell_tap_mode: this.tapMode(),
            upsell_parent: this.dataset.productHandle,
          });
          this.buildInlineAtc();
          this.updateInfoAtc();
          this.buildInfoDots();
          this.loadInfoReviews();
          this.bindRatingJump();
          this.bindDescToggle();

          if (!this._atcFloatBound) {
            this._atcFloatBound = true;
            this.infoSlot.addEventListener('scroll', () => this.syncAtcFloat(), { passive: true });
          }
          this.syncAtcFloat();

          this.infoDrawer.hidden = false;
          // Force a reflow so the transition runs from the hidden state.
          void this.infoDrawer.offsetWidth;
          this.infoDrawer.classList.add('is-open');
          document.body.classList.add('overflow-hidden');

          document.addEventListener('keydown', this.handleInfoKeydown);

          const closeBtn = this.infoDrawer.querySelector('[data-upsell-info-close]');
          if (closeBtn) closeBtn.focus();
        }

        closeInfo() {
          if (!this.infoDrawer || !this.infoDrawer.classList.contains('is-open')) return;
          this.closeZoom();

          this.infoDrawer.classList.remove('is-open');
          document.body.classList.remove('overflow-hidden');
          document.removeEventListener('keydown', this.handleInfoKeydown);

          let settled = false;
          const finish = (event) => {
            if (settled) return;
            if (event && event.target !== this.infoPanel) return;
            settled = true;
            clearTimeout(fallback);
            this.infoDrawer.hidden = true;
            if (this.infoSlot) this.infoSlot.replaceChildren();
            this.infoPanel.removeEventListener('transitionend', finish);
          };
          // transitionend is the primary signal; the timeout covers reduced-motion
          // and any interrupted transition so the drawer always fully resets.
          const fallback = setTimeout(finish, 450);
          this.infoPanel.addEventListener('transitionend', finish);

          // Restore focus only for keyboard closes: a programmatic focus after
          // a tap paints the focus ring around "More info" for pointer users.
          if (this._closedViaKeyboard && this.infoTrigger && typeof this.infoTrigger.focus === 'function') {
            this.infoTrigger.focus();
          }
          this._closedViaKeyboard = false;
          this.infoTrigger = null;
        }

        handleInfoKeydown(event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this._closedViaKeyboard = true;
            this.closeInfo();
            return;
          }

          if (event.key !== 'Tab') return;

          const focusable = this.infoPanel.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (!focusable.length) return;

          const first = focusable[0];
          const last = focusable[focusable.length - 1];

          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }

      customElements.define('product-upsell', ProductUpsell);
    }
